import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import WebMap from '@arcgis/core/WebMap';
import Search from '@arcgis/core/widgets/Search';
import { ensureArcgisAuth } from '../../../../../../utils/arcgisAuth';

const WebMapArcgis = ({ rmkValue }) => {
  const mapRef = useRef(null);
  const view = useRef(null);
  const pointLayerRef = useRef(null);

  // `esriCredential` is now an "auth ready" flag — the actual ArcGIS token is
  // registered globally via ensureArcgisAuth(). Kept as state so downstream
  // effects re-run once auth is in place.
  const [esriCredential, setEsriCredential] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  const {
    searchProgressStatus,
    user,
    selectMinistry,
    selectDepartment,
    selectProgram,
    activeRmk,
  } = useSelector((state) => ({
    user: state.userReducer.user,
    searchProgressStatus: state.dashboardReducer.searchProgressStatus,
    selectMinistry: state.dashboardReducer.selectMinistry,
    selectDepartment: state.dashboardReducer.selectDepartment,
    selectProgram: state.dashboardReducer.selectProgram,
    activeRmk: state.rmkReducer.activeRmk.number,
  }));

  // Prefer the dashboard's currently-selected RMK (passed in as a prop);
  // fall back to the system-wide active RMK if the prop isn't supplied.
  const selectedRmk = rmkValue?.number ?? rmkValue ?? activeRmk;

  // Initialize authentication once via the shared broker. Replaces the prior
  // OAuth popup which consumed an ArcGIS named-user seat per SMEPPS user.
  useEffect(() => {
    let cancelled = false;
    ensureArcgisAuth()
      .then(() => {
        if (!cancelled) setEsriCredential({ ready: true });
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('ArcGIS auth failed:', error);
          setAuthError(error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const popupProjectInfo = {
    title: '{name}',
    content: [
      {
        type: 'fields',
        fieldInfos: [
          {
            fieldName: 'name',
            label: 'Nama Projek',
            visible: true,
          },
        ],
      },
    ],
  };

  // Use simple-marker instead of picture-marker for color visual variables to work
  const renderer = {
    type: 'simple',
    symbol: {
      type: 'simple-marker',
      size: 10,
      color: '#2196f3',
      outline: {
        color: '#ffffff',
        width: 1.5,
      },
    },
    visualVariables: [
      {
        type: 'color',
        field: 'progress_status',
        stops: [
          { value: 8, color: '#43a047' },
          { value: 7, color: '#81c784' },
          { value: 6, color: '#90caf9' },
          { value: 5, color: '#2196f3' },
          { value: 4, color: '#ffc107' },
          { value: 3, color: '#f44336' },
          { value: 2, color: '#607d8b' },
          { value: 1, color: '#b0bec5' },
        ],
      },
    ],
  };

  // Helper function for SQL-safe values
  const sqlValue = (value) => {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value))) {
      return String(value);
    }
    return `'${String(value).replace(/'/g, "''")}'`;
  };

  // Improved definition expression with proper grouping
  const getDefinitionExpression = () => {
    const clauses = [];

    // Access constraints - properly group OR conditions
    const accessClauses = [];

    if (!isEmpty(user?.user_access_to_executing_agencies)) {
      const departments = user.user_access_to_executing_agencies
        .filter((item) => item?.id)
        .map((item) => `department_id = ${sqlValue(item.id)}`);
      if (departments.length > 0) {
        accessClauses.push(`(${departments.join(' OR ')})`);
      }
    }

    if (!isEmpty(user?.user_access_to_ministries)) {
      const ministries = user.user_access_to_ministries
        .filter((item) => item?.code)
        .map((item) => `ministry_id = ${sqlValue(item.code)}`);
      if (ministries.length > 0) {
        accessClauses.push(`(${ministries.join(' OR ')})`);
      }
    }

    // Combine access clauses with OR
    if (accessClauses.length > 0) {
      clauses.push(`(${accessClauses.join(' OR ')})`);
    }

    // UI filters - check for valid values (including 0)
    const hasValue = (v) => v !== null && v !== undefined && String(v) !== '';

    clauses.push(`rmk_id = ${selectedRmk}`);

    if (hasValue(searchProgressStatus)) {
      clauses.push(`progress_status = ${sqlValue(searchProgressStatus)}`);
    }
    if (hasValue(selectMinistry)) {
      clauses.push(`ministry_id = ${sqlValue(selectMinistry)}`);
    }
    if (hasValue(selectDepartment)) {
      clauses.push(`department_id = ${sqlValue(selectDepartment)}`);
    }
    if (hasValue(selectProgram)) {
      clauses.push(`program_id = ${sqlValue(selectProgram)}`);
    }

    return clauses.join(' AND ');
  };

  // Initialize map once after authentication
  useEffect(() => {
    if (!esriCredential || isMapInitialized || !mapRef.current) return;

    let cancelled = false;

    const pointLayer = new FeatureLayer({
      url: 'https://smepps-geo.sabah.gov.my/arcmanager/rest/services/vw_projects/FeatureServer/0',
      renderer,
      popupTemplate: popupProjectInfo,
      outFields: ['name', 'progress_status', 'ministry_id', 'department_id', 'program_id'],
    });

    const districtLayer = new FeatureLayer({
      url: 'https://services7.arcgis.com/imUjhC2BWfjR1bCA/arcgis/rest/services/SbhDistrictBdy/FeatureServer/0',
    });

    // Set initial filter
    pointLayer.definitionExpression = getDefinitionExpression();
    pointLayerRef.current = pointLayer;

    const webMap = new WebMap({
      basemap: 'streets',
      layers: [districtLayer, pointLayer],
    });

    view.current = new MapView({
      container: mapRef.current,
      map: webMap,
      center: [117.017156, 5.489928],
      zoom: 7.5,
    });

    view.current
      .when(() => {
        if (cancelled) return;

        const searchWidget = new Search({
          view: view.current,
          allPlaceholder: 'Daerah',
          includeDefaultSources: false,
          sources: [
            {
              layer: districtLayer,
              searchFields: ['Dist_Name'],
              displayField: 'Dist_Name',
              exactMatch: false,
              placeholder: 'cth: Kota Kinabalu',
              suggestionTemplate: '{Dist_Name}',
            },
          ],
        });

        view.current.ui.add(searchWidget, {
          position: 'top-right',
          index: 2,
        });

        setIsMapInitialized(true);
      })
      .catch((error) => {
        if (cancelled || error.name === 'AbortError') return;
        console.error('Map initialization failed:', error);
      });

    // Cleanup function
    return () => {
      cancelled = true;
      if (view.current) {
        view.current.destroy();
        view.current = null;
      }
      pointLayerRef.current = null;
      setIsMapInitialized(false);
    };
  }, [esriCredential]);

  // Update filters without recreating the map
  useEffect(() => {
    if (pointLayerRef.current && isMapInitialized) {
      const newExpression = getDefinitionExpression();
      pointLayerRef.current.definitionExpression = newExpression;
    }
  }, [
    searchProgressStatus,
    selectMinistry,
    selectDepartment,
    selectProgram,
    selectedRmk,
    user,
    isMapInitialized,
  ]);

  // Show loading or error states
  if (authError) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#f44336',
        }}
      >
        Authentication failed. Please refresh the page.
      </div>
    );
  }

  if (!esriCredential) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Authenticating...
      </div>
    );
  }

  return <div ref={mapRef} style={{ height: '100%', width: '100%', padding: 0, margin: 0 }} />;
};

export default WebMapArcgis;
